import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../lib/api";
import { CheckCircle, AlertCircle, Mail, MapPin, MessageSquare } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";

const PRODUCTS = ["SYNQ Projects", "SYNQ Evolve", "SYNQ AI", "SYNQ Strategy", "SYNQ Risk", "General Enquiry"];

export default function ContactPage() {
  const [params] = useSearchParams();
  const isDemo = params.get("intent") === "demo";
  const preselectProduct = params.get("product") || "";

  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", team_size: "",
    product: preselectProduct || (isDemo ? "SYNQ Projects" : "General Enquiry"),
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (preselectProduct) setForm((f) => ({ ...f, product: preselectProduct }));
  }, [preselectProduct]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/api/demo-requests`, { ...form, source: window.location.host });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute -top-40 left-0 w-[700px] h-[700px] rounded-full bg-blue-100 dark:bg-blue-950/40 blur-3xl opacity-40" />
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-20">
          <div className="grid md:grid-cols-5 gap-10 md:gap-14">
            {/* Left content */}
            <div className="md:col-span-2">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-4">
                {isDemo ? "Request Demo" : "Contact"}
              </div>
              <h1 className="font-outfit text-5xl md:text-6xl font-black text-slate-900 dark:text-white leading-[1.05]">
                {isDemo ? "Let's build your intelligence layer." : "Let's talk."}
              </h1>
              <p className="mt-6 font-ibm-plex text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-md">
                {isDemo
                  ? "Book a 30-minute private walkthrough — no slides, a live intelligence session on your context."
                  : "For partnerships, media, or general enquiries — send us a note and we'll be in touch within one business day."}
              </p>
              <div className="mt-10 space-y-5 text-sm font-ibm-plex text-slate-700 dark:text-slate-300">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-800 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </div>
                  <a href="mailto:hello@synqorg.com" className="hover:text-slate-900 dark:hover:text-white">hello@synqorg.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-800 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </div>
                  <span>Reply within 1 business day</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-800 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </div>
                  <span>RamRath Global (GA)</span>
                </div>
              </div>
            </div>

            {/* Right form */}
            <div className="md:col-span-3">
              {status === "success" ? (
                <div data-testid="contact-success" className="border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 rounded-3xl p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto flex items-center justify-center mb-6">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-outfit text-2xl font-black text-slate-900 dark:text-white">Thanks &mdash; we&rsquo;ll be in touch.</h2>
                  <p className="font-ibm-plex text-slate-600 dark:text-slate-400 mt-3 max-w-md mx-auto">
                    Your request has been received. A member of our team will reach out within one business day.
                  </p>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit} className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 md:p-10 space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Full Name</label>
                      <input data-testid="contact-name" type="text" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Work Email</label>
                      <input data-testid="contact-email" type="email" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Company</label>
                      <input data-testid="contact-company" type="text" value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Role</label>
                      <input data-testid="contact-role" type="text" value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        placeholder="e.g. VP Engineering"
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Team Size</label>
                      <select data-testid="contact-team-size" value={form.team_size}
                        onChange={(e) => setForm({ ...form, team_size: e.target.value })}
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white">
                        <option value="">Select…</option>
                        <option>1–10</option>
                        <option>11–50</option>
                        <option>51–200</option>
                        <option>201–1000</option>
                        <option>1000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Interested In</label>
                      <select data-testid="contact-product" value={form.product}
                        onChange={(e) => setForm({ ...form, product: e.target.value })}
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white">
                        {PRODUCTS.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5">Message</label>
                    <textarea data-testid="contact-message" rows={4} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us what you're trying to solve."
                      className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white resize-none" />
                  </div>
                  {status === "error" && (
                    <div data-testid="contact-error" className="border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 rounded-lg px-4 py-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="font-ibm-plex text-sm text-red-700 dark:text-red-300">{errorMsg}</span>
                    </div>
                  )}
                  <button data-testid="contact-submit" type="submit" disabled={status === "submitting"}
                    className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-outfit font-semibold px-6 py-3.5 rounded-full hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors disabled:opacity-60">
                    {status === "submitting" ? "Sending…" : (isDemo ? "Request Demo" : "Send Message")}
                  </button>
                  <p className="font-ibm-plex text-xs text-slate-500 dark:text-slate-500 text-center">
                    By submitting, you agree to be contacted by SYNQ / RamRath Global (GA).
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
